import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { CUSTOM_FAQ, CUSTOM_IDEAS, CUSTOM_STEPS, CUSTOMIZABLE } from "@/lib/content";
import { jsonLdScript, pageHead, PAGE_SEO, webPageGraph } from "@/lib/seo";
import { avifSrcSet, photos } from "@/lib/photos";

import { Reveal } from "@/components/site/reveal";
import { Photo } from "@/components/site/photo";
import { Eyebrow } from "@/components/site/eyebrow";
import { faqJsonLd, FaqList } from "@/components/site/faq-list";

const PHOTO_SIZES = "(min-width: 1024px) 25vw, 45vw";

export const Route = createFileRoute("/personalization")({
  component: PersonalizationPage,
  head: () => {
    const graph = webPageGraph(PAGE_SEO.personalization);
    return {
      ...pageHead({
        ...PAGE_SEO.personalization,
        preload: {
          href: photos.bridal.avif,
          imageSrcSet: avifSrcSet(photos.bridal),
          imageSizes: PHOTO_SIZES,
        },
        scripts: [
          jsonLdScript({
            ...graph,
            "@graph": [...graph["@graph"], faqJsonLd(CUSTOM_FAQ)],
          }),
        ],
      }),
    };
  },
});

function PersonalizationPage() {
  return (
    <main id="main" tabIndex={-1}>
      <section className="bg-plum py-16 text-plum-foreground sm:py-24 lg:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 lg:grid-cols-[1fr_1.05fr] lg:px-10">
          <Reveal>
            <Eyebrow>MADE JUST FOR THEM</Eyebrow>
            <h1 className="mt-5 text-4xl leading-[1.08] sm:text-5xl lg:text-6xl">
              Personalized pieces. <em className="font-display italic text-gold">Unforgettable</em>{" "}
              gifts.
            </h1>
            <p className="mt-6 max-w-xl leading-relaxed text-plum-foreground/75">
              Have a favorite color, a birthday theme, or an outfit you've pictured in your head?
              Ella takes made-to-order requests — custom colors, styles, and details — so the
              finished piece feels like it was made for one child only. Because it was.
            </p>
            <p className="mt-4 max-w-xl leading-relaxed text-plum-foreground/75">
              Everything is knit, crocheted, or sewn by Ella herself and sized for 18-inch American
              dolls, the same as the pieces in{" "}
              <Link to="/collection" className="text-gold underline underline-offset-4">
                the collection
              </Link>
              . Pricing and timing depend on what you'd like, and she'll confirm both with you
              before starting.
            </p>
            <div className="mt-8 stitch-rule text-gold" aria-hidden="true" />
            <Link
              to="/contact"
              search={{ interest: "custom" }}
              className="mt-8 inline-flex min-h-11 items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-plum-deep transition-transform hover:-translate-y-0.5"
            >
              Start a Custom Order <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Reveal>

          <Reveal delay={140}>
            <div className="grid grid-cols-2 gap-5 sm:gap-7">
              <figure>
                <Photo
                  {...photos.bridal}
                  alt="Handmade white crochet doll bridal gown with satin bow, tulle veil and pink flower crown"
                  eager
                  sizes={PHOTO_SIZES}
                  className="aspect-[4/5] w-full rounded-3xl object-cover shadow-[var(--shadow-lift)]"
                />
                <figcaption className="mt-3 text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-gold">
                  The pieces
                </figcaption>
              </figure>
              <figure className="sm:mt-12">
                <Photo
                  {...photos.dollBridal}
                  alt="18-inch doll wearing a handmade ivory bridal gown, veil and flower crown"
                  sizes={PHOTO_SIZES}
                  className="aspect-[4/5] w-full rounded-3xl object-cover shadow-[var(--shadow-lift)]"
                />
                <figcaption className="mt-3 text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-gold">
                  Styled on the doll
                </figcaption>
              </figure>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-secondary py-16 sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>WHAT CAN BE PERSONALIZED</Eyebrow>
            <h2 className="mt-5 text-4xl leading-[1.08] sm:text-5xl">
              Colors, styles, themes, and{" "}
              <em className="accent-underline font-display italic text-primary">occasions.</em>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              Every made-to-order piece is sized for 18-inch dolls and finished by hand. Share as
              much or as little detail as you like — Ella will help fill in the rest, including
              matching accessories like headbands, bonnets, and booties.
            </p>
          </Reveal>

          <ul className="mt-14 grid gap-5 sm:grid-cols-2">
            {CUSTOMIZABLE.map((item, i) => (
              <Reveal as="li" key={item.title} delay={i * 80}>
                <div className="h-full rounded-3xl border border-border/70 bg-card p-6 shadow-[var(--shadow-soft)]">
                  <h3 className="font-display text-lg">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.copy}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-16 sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>IDEAS TO START FROM</Eyebrow>
            <h2 className="mt-5 text-4xl leading-[1.08] sm:text-5xl">
              A little{" "}
              <em className="accent-underline font-display italic text-primary">inspiration.</em>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              These are examples of the kinds of requests people bring to Ella — not a fixed menu.
              Bring your own idea and she'll tell you what's possible.
            </p>
          </Reveal>

          <ul className="mt-14 grid gap-5 sm:grid-cols-2">
            {CUSTOM_IDEAS.map((item, i) => (
              <Reveal as="li" key={item.title} delay={(i % 2) * 80}>
                <div className="h-full rounded-3xl bg-secondary p-7">
                  <h3 className="font-display text-lg">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.copy}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-secondary py-16 sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>HOW A CUSTOM ORDER WORKS</Eyebrow>
            <h2 className="mt-5 text-4xl leading-[1.08] sm:text-5xl">
              From hello to{" "}
              <em className="accent-underline font-display italic text-primary">handmade.</em>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              There is no shopping cart — Ella works with you one-on-one, the same way she always
              has. A few notes about what you love is enough to begin.
            </p>
          </Reveal>

          <ol className="mt-14 grid gap-7 md:grid-cols-3">
            {CUSTOM_STEPS.map((s, i) => (
              <Reveal as="li" key={s.title} delay={i * 100}>
                <div className="h-full rounded-3xl border border-border/70 bg-card p-8 shadow-[var(--shadow-soft)]">
                  <span className="font-display text-4xl text-primary/40">0{i + 1}</span>
                  <div className="mt-5 stitch-rule text-primary" aria-hidden="true" />
                  <h3 className="mt-5 font-display text-xl">{s.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{s.copy}</p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-plum-deep py-16 text-plum-foreground sm:py-24 lg:py-32">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-[0.9fr_1fr] lg:px-10">
          <Reveal>
            <Eyebrow>WHY MADE-TO-ORDER</Eyebrow>
            <h2 className="mt-5 text-4xl leading-[1.08] sm:text-5xl">
              A gift that could only be{" "}
              <em className="font-display italic text-gold">theirs.</em>
            </h2>
          </Reveal>
          <Reveal delay={120} className="space-y-4 leading-relaxed text-plum-foreground/75">
            <p>
              A made-to-order outfit carries something a shelf gift can't: the knowledge that
              somebody thought about this one child, chose their color, and spent real hours
              stitching it. Children notice that, and so do the grandparents giving it.
            </p>
            <p>
              Because Ella makes one piece at a time, nothing is rushed through a factory line. The
              outfit arrives finished by the same pair of hands that started it, ready to wrap — and
              sturdy enough to survive being loved.
            </p>
            <p>
              Many pieces end up kept long after the child outgrows the doll, which is exactly the
              point of something handmade.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>CUSTOM ORDER QUESTIONS</Eyebrow>
            <h2 className="mt-5 text-4xl leading-[1.08] sm:text-5xl">
              Good to{" "}
              <em className="accent-underline font-display italic text-primary">know.</em>
            </h2>
          </Reveal>

          <FaqList items={CUSTOM_FAQ} />

          <Reveal className="mt-16">
            <div className="flex flex-col items-center gap-6 rounded-3xl bg-accent px-8 py-10 text-center md:flex-row md:justify-between md:text-left">
              <p className="max-w-2xl font-display text-xl leading-snug text-accent-foreground sm:text-2xl">
                Ready to start? Tell Ella what you have in mind and she will follow up personally.
              </p>
              <Link
                to="/contact"
                search={{ interest: "custom" }}
                className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
              >
                Start a Custom Order <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
